// Shared definition of the Hebrew health declaration & consent form.
// Used by the public patient page, the PDF summary and the admin viewer.

export type YesNo = 'yes' | 'no';

export interface HealthQuestion {
  key: string;
  label: string;
}

export const HEALTH_QUESTIONS: HealthQuestion[] = [
  { key: 'pregnancy', label: 'הריון או הנקה' },
  { key: 'heart', label: 'מחלות לב או לחץ דם' },
  { key: 'diabetes', label: 'סוכרת' },
  { key: 'epilepsy', label: 'אפילפסיה או התקפים' },
  { key: 'skin_disease', label: 'מחלות עור (פסוריאזיס, אקזמה וכו׳)' },
  { key: 'keloid', label: 'נטייה לצלקות קלואידיות' },
  { key: 'herpes', label: 'הרפס פעיל' },
  { key: 'autoimmune', label: 'מחלות אוטואימוניות' },
  { key: 'thyroid', label: 'בעיות בבלוטת התריס' },
  { key: 'oncology', label: 'טיפולי הקרנות או כימותרפיה' },
  { key: 'implants', label: 'שתלים מתכתיים או קעקועים באזור הטיפול' },
  { key: 'roaccutane', label: 'נטילת רואקוטן בשנה האחרונה' },
  { key: 'sun', label: 'חשיפה לשמש או שיזוף בשבועיים האחרונים' },
];

export const TREATMENT_TYPES = [
  'הסרת שיער בלייזר',
  'טיפולי פנים',
  'פילינג',
  'מיקרונידלינג',
  'רוקחות אישית',
  'אחר',
];

export interface ConsentItem {
  key: string;
  label: string;
}

export const CONSENT_ITEMS: ConsentItem[] = [
  {
    key: 'truthful',
    label: 'אני מצהירה שכל הפרטים שמסרתי נכונים ומלאים, ואעדכן את הקליניקה בכל שינוי במצב בריאותי.',
  },
  {
    key: 'risks',
    label: 'קיבלתי הסבר מלא על מהות הטיפול, התוצאות האפשריות ותופעות הלוואי, והבנתי אותו.',
  },
  {
    key: 'instructions',
    label: 'אני מתחייבת לפעול לפי הוראות הצוות לפני ואחרי הטיפול.',
  },
  {
    key: 'consent',
    label: 'אני מסכימה לביצוע הטיפול מרצוני החופשי.',
  },
  {
    key: 'privacy',
    label: 'אני מאשרת שמירת הפרטים הרפואיים שלי בקליניקה לצורך המשך טיפול, בהתאם למדיניות הפרטיות.',
  },
];

export type PhotoConsent = 'approved' | 'declined';

export interface HealthFormAnswers {
  full_name: string;
  id_number: string;
  birth_date: string;
  phone: string;
  email: string;
  address: string;
  questions: Record<string, YesNo>;
  medications: string;
  skin_condition: string;
  allergies: string;
  previous_treatments: string;
  notes: string;
  treatment_types: string[];
  consents: Record<string, boolean>;
  photo_consent: PhotoConsent | '';
  signed_at: string;
}

export function emptyAnswers(): HealthFormAnswers {
  return {
    full_name: '',
    id_number: '',
    birth_date: '',
    phone: '',
    email: '',
    address: '',
    questions: {},
    medications: '',
    skin_condition: '',
    allergies: '',
    previous_treatments: '',
    notes: '',
    treatment_types: [],
    consents: {},
    photo_consent: '',
    signed_at: '',
  };
}

export interface ValidationResult {
  ok: boolean;
  errors: Record<string, string>;
}

const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export function validateAnswers(a: HealthFormAnswers, hasSignature: boolean): ValidationResult {
  const errors: Record<string, string> = {};
  if (a.full_name.trim().length < 2) errors.full_name = 'נא למלא שם מלא';
  if (!/^\d{5,12}$/.test(a.id_number.trim())) errors.id_number = 'נא למלא מספר זהות תקין';
  if (!a.birth_date) errors.birth_date = 'נא למלא תאריך לידה';
  if (a.phone.replace(/\D/g, '').length < 9) errors.phone = 'נא למלא מספר טלפון תקין';
  if (!emailRe.test(a.email.trim())) errors.email = 'נא למלא כתובת אימייל תקינה';
  for (const q of HEALTH_QUESTIONS) {
    if (a.questions[q.key] !== 'yes' && a.questions[q.key] !== 'no') {
      errors[`questions.${q.key}`] = 'נא לבחור כן או לא';
    }
  }
  if (a.treatment_types.length === 0) errors.treatment_types = 'נא לבחור סוג טיפול אחד לפחות';
  for (const c of CONSENT_ITEMS) {
    if (!a.consents[c.key]) errors[`consents.${c.key}`] = 'נדרש אישור';
  }
  if (a.photo_consent !== 'approved' && a.photo_consent !== 'declined') {
    errors.photo_consent = 'נא לבחור אפשרות';
  }
  if (!hasSignature) errors.signature = 'נא לחתום בתיבת החתימה';
  return { ok: Object.keys(errors).length === 0, errors };
}

export const YES_NO_LABEL: Record<YesNo, string> = { yes: 'כן', no: 'לא' };

export const PHOTO_CONSENT_LABEL: Record<PhotoConsent, string> = {
  approved: 'מאשרת צילום ושימוש בתמונות לפני/אחרי',
  declined: 'לא מאשרת צילום',
};
